/**
 * Custom form section for ``HttpSinkConfig.outcome_rules`` (ADR-0008 PR3).
 *
 * Replaces the generic "type your JSON here" textarea with a table of
 * rules: each row has a name, a JSONata predicate, an outcome, and —
 * when outcome=branch — a branch picker that reads sibling
 * ``config.branches`` + the implicit ``succeeded`` / ``dead_letter``.
 *
 * Validation matches the backend OutcomeRule pydantic model:
 *   - name required, max 128 chars
 *   - when required, max 2048, must look like JSONata (compile happens
 *     server-side, frontend does only shape checks)
 *   - outcome ∈ {succeeded, failed_retryable, failed_terminal, branch}
 *   - branch_id required iff outcome=branch; must reference a known
 *     branch (implicit OR declared)
 *
 * Save-time refusal still comes from the backend — the editor's job is
 * to make invalid shapes IMPOSSIBLE to express cleanly, not to be the
 * source of truth.
 */
import { Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


// Mirrors backend hub.workflow.catalog.OutcomeKind exactly.
export type OutcomeKind =
  | "succeeded"
  | "failed_retryable"
  | "failed_terminal"
  | "branch";

export interface OutcomeRule {
  name: string;
  when: string;
  outcome: OutcomeKind;
  /** Required when outcome === "branch", otherwise null. */
  branch_id: string | null;
}

// Mirrors backend IMPLICIT_BRANCH_IDS — present on every sink.
const IMPLICIT_BRANCH_IDS = ["succeeded", "dead_letter"] as const;

const OUTCOME_OPTIONS: ComboboxOption[] = [
  {
    value: "succeeded",
    label: "Succeeded",
    description: "Mark delivery OK. `succeeded` branch fires.",
  },
  {
    value: "failed_retryable",
    label: "Failed (retryable)",
    description: "Drain retries with backoff until budget is exhausted.",
  },
  {
    value: "failed_terminal",
    label: "Failed (terminal)",
    description: "Dead-letter immediately. `dead_letter` branch fires.",
  },
  {
    value: "branch",
    label: "Branch (custom)",
    description: "Route to a custom branch id (PR2+).",
  },
];


export interface OutcomeRulesEditorProps {
  rules: OutcomeRule[];
  /** Sibling-declared branches (without the two implicit ones). Used
   *  to populate the branch picker when outcome=branch. */
  branchIds: string[];
  onChange: (next: OutcomeRule[]) => void;
}


export function OutcomeRulesEditor({
  rules,
  branchIds,
  onChange,
}: OutcomeRulesEditorProps) {
  // All valid branch targets a rule can reference: implicit + declared.
  const branchOptions = React.useMemo<ComboboxOption[]>(() => {
    const seen = new Set<string>();
    const out: ComboboxOption[] = [];
    for (const id of IMPLICIT_BRANCH_IDS) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ value: id, label: id, description: "implicit" });
    }
    for (const id of branchIds) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({ value: id, label: id, description: "custom" });
    }
    return out;
  }, [branchIds]);

  // Rules whose branch_id no longer exists in the picker — surfaces in
  // a warning so the operator notices before save fails.
  const orphanRules = React.useMemo(() => {
    const valid = new Set(branchOptions.map((o) => o.value));
    return rules.filter(
      (r) => r.outcome === "branch" && r.branch_id && !valid.has(r.branch_id),
    );
  }, [rules, branchOptions]);

  function update(idx: number, patch: Partial<OutcomeRule>) {
    onChange(
      rules.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...patch };
        // Coherence guard: switching outcome AWAY from "branch" must
        // null the branch_id, switching TO "branch" leaves it empty so
        // the picker is forced.
        if (patch.outcome && patch.outcome !== "branch") {
          next.branch_id = null;
        }
        return next;
      }),
    );
  }

  function add() {
    onChange([
      ...rules,
      {
        name: `Rule ${rules.length + 1}`,
        when: "",
        outcome: "succeeded",
        branch_id: null,
      },
    ]);
  }

  function remove(idx: number) {
    onChange(rules.filter((_, i) => i !== idx));
  }

  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Outcome rules</Label>
          <p className="text-[11px] text-muted-foreground">
            Evaluated top to bottom — first match wins. Bindings:{" "}
            <span className="font-mono">$.status</span>,{" "}
            <span className="font-mono">$.headers</span>,{" "}
            <span className="font-mono">$.body</span>. If nothing
            matches, the HTTP status alone decides (2xx OK, 5xx retry,
            4xx terminal).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Add rule
        </Button>
      </div>

      {orphanRules.length > 0 && (
        <Alert tone="warning" title="Rules point at unknown branches">
          {orphanRules.length} rule{orphanRules.length > 1 ? "s" : ""}{" "}
          route to branch{orphanRules.length > 1 ? "es" : ""}{" "}
          <span className="font-mono">
            {orphanRules.map((r) => r.branch_id).join(", ")}
          </span>{" "}
          that aren't declared on this sink. Add the branch below, or
          change the rule outcome to something else.
        </Alert>
      )}

      <div className="space-y-2">
        {rules.map((r, idx) => (
          <RuleRow
            key={idx}
            rule={r}
            idx={idx}
            total={rules.length}
            branchOptions={branchOptions}
            onChange={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
            onMoveUp={() => move(idx, -1)}
            onMoveDown={() => move(idx, +1)}
          />
        ))}
        {rules.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
            No rules yet. HTTP status alone decides the outcome (2xx OK,
            5xx retry, 4xx terminal). Add a rule to reclassify based on
            response body.
          </div>
        )}
      </div>
    </div>
  );
}


function RuleRow({
  rule,
  idx,
  total,
  branchOptions,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  rule: OutcomeRule;
  idx: number;
  total: number;
  branchOptions: ComboboxOption[];
  onChange: (patch: Partial<OutcomeRule>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-2 space-y-2">
      <div className="flex items-center gap-1">
        <div className="flex flex-col text-muted-foreground/60">
          <button
            type="button"
            disabled={idx === 0}
            onClick={onMoveUp}
            className="text-[8px] leading-none hover:text-foreground disabled:opacity-30"
            title="Move up"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={idx === total - 1}
            onClick={onMoveDown}
            className="text-[8px] leading-none hover:text-foreground disabled:opacity-30"
            title="Move down"
          >
            ▼
          </button>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">
          #{idx + 1}
        </span>

        <div className="ml-1 flex-1">
          <Input
            value={rule.name}
            onChange={(e) => onChange({ name: e.target.value.slice(0, 128) })}
            placeholder="Rule name (shown in trace)"
            className="h-7 text-xs"
          />
        </div>

        <div className="w-44">
          <Combobox
            options={OUTCOME_OPTIONS}
            value={rule.outcome}
            onChange={(v) => onChange({ outcome: (v ?? "succeeded") as OutcomeKind })}
            placeholder="Outcome"
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label="Remove rule"
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
        </Button>
      </div>

      <textarea
        rows={2}
        value={rule.when}
        onChange={(e) => onChange({ when: e.target.value.slice(0, 2048) })}
        placeholder="JSONata predicate — e.g. $.body.success = false"
        className="block w-full resize-y rounded border border-border bg-background px-2 py-1 font-mono text-[11px]"
        aria-label="JSONata predicate"
      />

      {rule.outcome === "branch" && (
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-muted-foreground shrink-0">
            Branch:
          </Label>
          <div className="flex-1">
            <Combobox
              options={branchOptions}
              value={rule.branch_id || null}
              onChange={(v) => onChange({ branch_id: v })}
              placeholder="Pick a branch…"
              emptyText="Add a branch below first."
            />
          </div>
        </div>
      )}
    </div>
  );
}
